import type { FC } from 'react';
import { Avatar, Box, IconButton, Tooltip, Typography } from '@mui/material';
import {
  ArrowUpward as ArrowUpwardIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { format, isValid, parseISO } from 'date-fns';
import type { RedditComment } from '../types';

interface RedditCommentItemProps {
  comment: RedditComment;
}

const formatCount = (n: number): string => {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
};

const formatDate = (value: string, pattern: string): string => {
  if (!value) return '';
  const date = parseISO(value);
  return isValid(date) ? format(date, pattern) : '';
};

const RedditCommentItem: FC<RedditCommentItemProps> = ({ comment }) => {
  const authorInitial = comment.author.replace(/^u\//, '').charAt(0).toUpperCase() || '?';

  return (
    <Box sx={{ py: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Avatar
          sx={{
            width: 28,
            height: 28,
            fontSize: '0.75rem',
            bgcolor: 'primary.main',
            opacity: 0.7,
          }}
        >
          {authorInitial}
        </Avatar>
        <Typography variant="caption" fontWeight={600}>
          {comment.author}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
          {formatDate(comment.commented_at, 'MMM d, yyyy')}
        </Typography>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, pl: 4.5, whiteSpace: 'pre-line' }}>
        {comment.body}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, pl: 4.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <ArrowUpwardIcon sx={{ fontSize: 13, color: '#FF4500' }} />
          <Typography variant="caption" fontWeight={600} color="text.secondary">
            {formatCount(comment.upvotes_count)}
          </Typography>
        </Box>
        {comment.url && (
          <Tooltip title="Open comment on Reddit">
            <IconButton
              component="a"
              href={comment.url}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              aria-label="Open comment on Reddit"
              sx={{ ml: 'auto' }}
            >
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default RedditCommentItem;
